<!-- Parsec Cloud (https://parsec.cloud) Copyright (c) AGPL-3.0-only 2016-present Scille SAS -->

<template>
  <div class="authentication-container">
    <template v-if="querying">
      <ms-spinner />
    </template>
    <template v-else-if="currentDevice">
      <div class="authentication-content">
        <div class="authentication-item">
          <div class="authentication-item-header">
            <ion-text class="authentication-item-header__label title-h4">
              {{ $msTranslate('Authentication.label') }}
            </ion-text>
          </div>
          <div class="authentication-item-content">
            <ion-text class="authentication-item-content__description method body-lg">
              {{ $msTranslate(authMethodLabel) }}
            </ion-text>
            <ion-button
              id="change-authentication-button"
              class="authentication-item-button button-default button-large secondary-button"
              fill="clear"
              @click="openChangeAuthentication()"
            >
              <ion-text class="authentication-item-button__label">
                {{ $msTranslate('Authentication.changeAuthenticationButton') }}
              </ion-text>
            </ion-button>
          </div>
        </div>

        <!-- MFA -->
        <div
          class="authentication-item mfa"
          :class="currentDevice.totpOpaqueKeyId ? 'mfa-active' : 'mfa-inactive'"
        >
          <div
            class="authentication-item-header"
            v-if="totpStatus"
          >
            <ion-text class="authentication-item-header__label title-h4">
              {{ $msTranslate('Authentication.mfa.title') }}
              <span
                v-if="currentDevice.totpOpaqueKeyId"
                class="tag"
              >
                {{ $msTranslate('Authentication.mfa.active') }}
              </span>
              <span
                v-if="!currentDevice.totpOpaqueKeyId"
                class="tag button-medium"
              >
                {{ $msTranslate('Authentication.mfa.inactive') }}
              </span>
            </ion-text>
            <ms-report-text
              class="authentication-item-header__report"
              v-show="totpStatus?.tag === TOTPSetupStatusTag.Confirmed && !currentDevice.totpOpaqueKeyId"
              :theme="MsReportTheme.Info"
            >
              {{ $msTranslate('Authentication.mfa.inactiveInfo') }}
            </ms-report-text>
          </div>
          <div
            class="authentication-item-header mfa-error"
            v-else
          >
            <ion-text class="authentication-item-header__label title-h4">{{ $msTranslate('Authentication.mfa.title') }}</ion-text>
            <ms-report-text :theme="MsReportTheme.Error">{{ $msTranslate('Authentication.mfa.error.emptyStatus') }}</ms-report-text>
          </div>
          <ms-report-text
            class="authentication-item--mandatory"
            v-if="mandatoryTotp"
            :theme="MsReportTheme.Warning"
          >
            {{ $msTranslate('Authentication.mfa.info') }}
          </ms-report-text>
          <div class="authentication-item-content">
            <ion-text class="authentication-item-content__description body-lg">
              {{ $msTranslate('Authentication.mfa.description') }}
            </ion-text>
            <ion-button
              v-if="totpStatus && totpStatus.tag === TOTPSetupStatusTag.Unconfirmed"
              id="totp-setup-button"
              class="authentication-item-button button-default button-large primary-button"
              fill="clear"
              @click="setupTotp"
            >
              <ion-text class="authentication-item-button__label">
                {{ $msTranslate('Authentication.mfa.buttons.configure') }}
              </ion-text>
            </ion-button>
            <ion-toggle
              :disabled="totpActivated && mandatoryTotp"
              v-model="totpActivated"
              @ion-change="toggleTotp"
              v-if="totpStatus && totpStatus.tag === TOTPSetupStatusTag.Confirmed"
            />
          </div>
        </div>
      </div>
    </template>
    <template v-if="error">
      <div class="device-not-found">
        <ion-icon :icon="warning" />
        <ion-text class="item-container__text body">
          {{ $msTranslate(error) }}
        </ion-text>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  AvailableDevice,
  AvailableDeviceTypeTag,
  getCurrentAvailableDevice,
  getCurrentServerConfig,
  getPrimaryProtectionTypeForDeviceType,
  getTotpStatus,
  ServerConfig,
  TOTPSetupStatus,
  TOTPSetupStatusTag,
} from '@/parsec';
import { Information, InformationLevel, InformationManager, InformationManagerKey, PresentationMode } from '@/services/informationManager';
import ActivateTotpModal from '@/views/totp/ActivateTotpModal.vue';
import DeleteTotpModal from '@/views/totp/DeleteTotpModal.vue';
import UpdateAuthenticationModal from '@/views/users/UpdateAuthenticationModal.vue';
import { IonButton, IonIcon, IonText, IonToggle, modalController, ToggleCustomEvent } from '@ionic/vue';
import { warning } from 'ionicons/icons';
import { Answer, askQuestion, MsModalResult, MsReportText, MsReportTheme, MsSpinner, Translatable } from 'megashark-lib';
import { computed, inject, onMounted, Ref, ref } from 'vue';

const currentDevice: Ref<AvailableDevice | null> = ref(null);
const informationManager: Ref<InformationManager> = inject(InformationManagerKey)!;
const error = ref('');
const querying = ref(true);
const totpStatus = ref<TOTPSetupStatus | undefined>(undefined);
const totpActivated = ref(false);
const serverConfig = ref<ServerConfig | undefined>(undefined);
const mandatoryTotp = computed(() => {
  if (!serverConfig.value || !currentDevice.value) {
    return false;
  }
  const protectionType = getPrimaryProtectionTypeForDeviceType(currentDevice.value.ty.tag);
  if (!protectionType) {
    return false;
  }
  return serverConfig.value.doesAuthMethodRequireTotp(protectionType);
});
const authMethodLabel = computed(() => {
  if (!currentDevice.value) return '';

  const methodLabels: Record<AvailableDeviceTypeTag, Translatable> = {
    [AvailableDeviceTypeTag.Keyring]: 'Authentication.useKeyring',
    [AvailableDeviceTypeTag.Password]: 'Authentication.usePassword',
    [AvailableDeviceTypeTag.PKI]: 'Authentication.useSmartcard',
    [AvailableDeviceTypeTag.OpenBao]: 'Authentication.useSso',
    [AvailableDeviceTypeTag.AccountVault]: '',
    [AvailableDeviceTypeTag.Recovery]: '',
  };

  return methodLabels[currentDevice.value.ty.tag] || '';
});

async function openChangeAuthentication(): Promise<void> {
  if (!currentDevice.value) {
    return;
  }

  if (currentDevice.value.ty.tag === AvailableDeviceTypeTag.OpenBao) {
    if (!serverConfig.value?.openbao) {
      error.value = 'Authentication.invalidOpenBaoData';
      return;
    }
  }

  if (currentDevice.value.ty.tag === AvailableDeviceTypeTag.PKI) {
    const answer = await askQuestion('Authentication.method.smartcard.warn.title', 'Authentication.method.smartcard.warn.subtitle', {
      yesText: 'Authentication.method.smartcard.warn.yes',
      noText: 'Authentication.method.smartcard.warn.no',
    });

    if (answer === Answer.No) {
      return;
    }
  }

  const modal = await modalController.create({
    component: UpdateAuthenticationModal,
    cssClass: 'change-authentication-modal',
    componentProps: {
      currentDevice: currentDevice.value,
      informationManager: informationManager.value,
      serverConfig: serverConfig.value,
    },
  });
  await modal.present();
  const { role } = await modal.onWillDismiss();
  await modal.dismiss();
  if (role === MsModalResult.Confirm) {
    const result = await getCurrentAvailableDevice();
    if (result.ok) {
      currentDevice.value = result.value;
      error.value = '';
    } else {
      error.value = 'MyProfilePage.errors.failedToRetrieveInformation';
    }
  }
}

onMounted(async () => {
  await refresh();
});

async function refresh(): Promise<void> {
  try {
    querying.value = true;
    const totpStatusResult = await getTotpStatus();

    totpStatus.value = totpStatusResult.ok ? totpStatusResult.value : undefined;

    const serverConfigResult = await getCurrentServerConfig();
    if (serverConfigResult.ok) {
      serverConfig.value = serverConfigResult.value;
    }

    const deviceResult = await getCurrentAvailableDevice();

    if (!deviceResult.ok) {
      informationManager.value.present(
        new Information({
          message: 'MyProfilePage.errors.failedToRetrieveInformation',
          level: InformationLevel.Error,
        }),
        PresentationMode.Toast,
      );
      error.value = 'MyProfilePage.errors.failedToRetrieveInformation';
    } else {
      error.value = '';
      currentDevice.value = deviceResult.value;
      totpActivated.value = currentDevice.value.totpOpaqueKeyId !== null;
    }
  } finally {
    querying.value = false;
  }
}

async function toggleTotp(event: ToggleCustomEvent): Promise<void> {
  // The event has the new value of the toggle: when checked is false, it means
  // it was true and was just switched to false.
  if (!event.detail.checked) {
    await deleteTotp();
  } else {
    await activateTotp();
  }
  await refresh();
}

async function activateTotp(): Promise<void> {
  if (!currentDevice.value || !totpStatus.value) {
    return;
  }
  const modal = await modalController.create({
    component: ActivateTotpModal,
    cssClass: 'activate-totp-modal',
    componentProps: {
      params: {
        mode: 'activate',
        device: currentDevice.value,
      },
    },
    canDismiss: true,
    backdropDismiss: true,
    showBackdrop: true,
  });
  await modal.present();
  const { role } = await modal.onDidDismiss();
  await modal.dismiss();

  if (role !== MsModalResult.Confirm) {
    return;
  }
  await refresh();
  informationManager.value.present(
    new Information({
      message: 'Authentication.mfa.mfaSuccess.description',
      level: InformationLevel.Success,
    }),
    PresentationMode.Toast,
  );
}

async function setupTotp(): Promise<void> {
  if (!currentDevice.value) {
    return;
  }
  const modal = await modalController.create({
    component: ActivateTotpModal,
    cssClass: 'activate-totp-modal',
    componentProps: {
      params: {
        mode: 'setup',
        device: currentDevice.value,
      },
    },
    canDismiss: true,
    backdropDismiss: true,
    showBackdrop: true,
  });
  await modal.present();
  const { role } = await modal.onDidDismiss();
  await modal.dismiss();

  if (role !== MsModalResult.Confirm) {
    return;
  }
  await refresh();
  informationManager.value.present(
    new Information({
      message: 'Authentication.mfa.mfaSuccess.description',
      level: InformationLevel.Success,
    }),
    PresentationMode.Toast,
  );
}

async function deleteTotp(): Promise<void> {
  if (!currentDevice.value) {
    return;
  }
  const modal = await modalController.create({
    component: DeleteTotpModal,
    cssClass: 'delete-totp',
    componentProps: {
      device: currentDevice.value,
    },
    canDismiss: true,
    backdropDismiss: true,
    showBackdrop: true,
  });
  await modal.present();
  const { role } = await modal.onDidDismiss();
  await modal.dismiss();

  if (role !== MsModalResult.Confirm) {
    return;
  }
  await refresh();
  informationManager.value.present(
    new Information({
      message: 'Authentication.mfa.mfaSuccess.deleteOk',
      level: InformationLevel.Success,
    }),
    PresentationMode.Toast,
  );
}
</script>

<style scoped lang="scss">
.authentication-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  border-radius: var(--parsec-radius-12);
  background: var(--parsec-color-light-secondary-white);
  padding: 1.5rem;
  box-shadow:
    0 1px 1px 0 rgba(0, 0, 0, 0.05),
    0 1px 4px 0 rgba(0, 0, 0, 0.03),
    0 0 1px 0 rgba(0, 0, 0, 0.2);
}

.authentication-content {
  display: flex;
  flex-direction: column;
}

.authentication-item {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &:not(:last-child) {
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--parsec-color-light-secondary-medium);
    margin-bottom: 1.5rem;
  }

  @include ms.responsive-breakpoint('lg') {
    flex-direction: column;
    align-items: stretch;
  }

  @include ms.responsive-breakpoint('sm') {
    flex-direction: row;
    align-items: center;
  }

  @include ms.responsive-breakpoint('sm') {
    align-items: stretch;
    flex-direction: column;
  }

  &-header {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex-grow: 1;

    &__label {
      color: var(--parsec-color-light-secondary-text);
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    &__report {
      padding: 0.5rem 1rem 0.5rem 0.625rem !important;
    }
  }

  &-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    @include ms.responsive-breakpoint('md') {
      flex-direction: column;
      align-items: start;
      gap: 0.75rem;
    }

    &__description {
      color: var(--parsec-color-light-secondary-hard-grey);

      &.method {
        font-style: italic;
      }
    }
  }

  &-button {
    width: fit-content;
    height: fit-content;
    box-shadow: var(--parsec-shadow-input);

    &.primary-button {
      --background: var(--parsec-color-light-secondary-text);
      --background-hover: var(--parsec-color-light-secondary-contrast);
      color: var(--parsec-color-light-secondary-white);
    }

    &.secondary-button {
      --background: var(--parsec-color-light-secondary-white);
      --background-hover: var(--parsec-color-light-secondary-medium);
      border: 1px solid var(--parsec-color-light-secondary-disabled);
      color: var(--parsec-color-light-secondary-text);
      border-radius: var(--parsec-radius-8);
    }

    &::part(native) {
      font-size: 0.9375rem;
      padding: 0.75rem 0.825rem;
    }

    @include ms.responsive-breakpoint('md') {
      width: 100%;
    }
  }

  &.mfa {
    .authentication-item-text__label {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    &-active .tag,
    &-inactive .tag {
      flex-shrink: 0;
      padding: 0.25rem 0.5rem;
      border-radius: var(--parsec-radius-12);
      white-space: nowrap;
      margin-left: auto;
    }

    &-active .tag {
      color: var(--parsec-color-light-success-700);
      background: var(--parsec-color-light-success-100);
    }

    &-inactive .tag {
      color: var(--parsec-color-light-danger-700);
      background: var(--parsec-color-light-danger-50);
    }

    .authentication-item-button {
      @include ms.responsive-breakpoint('sm') {
        position: static;
        transform: none;
      }
    }
  }
}

.device-not-found {
  display: flex;
  align-items: center;
  background: var(--parsec-color-light-danger-50);
  color: var(--parsec-color-light-danger-700);
  padding: 0.5rem 1rem;
  border-radius: var(--parsec-radius-8);
  gap: 0.5rem;

  ion-text {
    padding: 0.25rem 0;
  }

  ion-icon {
    font-size: 1.25rem;
  }
}

.mfa-error {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>
